import type { UIMessage } from "@ai-sdk/react";
import { SPEC_DATA_PART_TYPE } from "@json-render/core";
import type { RefObject } from "react";
import {
  AttachmentBubbleRow,
  type AttachmentBubbleRowItem,
} from "@/components/ui/chat/AttachmentBubbles";
import StreamingQuipRow from "@/components/ui/chat/StreamingQuipRow";
import ThinkingBubble from "@/components/ui/chat/ThinkingBubble";
import UserBubble from "@/components/ui/chat/UserBubble";
import type { HeraldChatMessageMetadata } from "@/lib/hooks/chat/useRetrievePreviousConversation";
import { useStableSettledAndStreamingMessages } from "@/lib/hooks/chat/useStableSettledAndStreamingMessages";
import {
  buildMessageSegments,
  type MessagePart,
} from "@/lib/utils/aiChatUtils";
import { cn } from "@/lib/utils/cn";
import { AssistantMessageBubble } from "./AssistantMessageBubble";

interface FilePart extends Record<string, unknown> {
  type: "file";
  mediaType?: string;
  filename?: string;
  name?: string;
  url?: string;
}

const getMessageParts = (message: UIMessage): ReadonlyArray<MessagePart> =>
  ("parts" in message ? message.parts : []) as ReadonlyArray<MessagePart>;

const getMessageCreatedAt = (message: UIMessage): string | undefined =>
  (message.metadata as HeraldChatMessageMetadata | undefined)?.createdAt;

interface HeraldChatUserMessageRowProps {
  message: UIMessage;
  wrapUserBubble?: boolean;
}

const HeraldChatUserMessageRow = ({
  message,
  wrapUserBubble = false,
}: HeraldChatUserMessageRowProps) => {
  const parts = getMessageParts(message);
  const segments = buildMessageSegments(parts, SPEC_DATA_PART_TYPE);
  const firstSegment = segments[0];
  const userText =
    segments.length === 1 && firstSegment?.kind === "text"
      ? firstSegment.text
      : null;
  const attachments = parts
    .filter((part): part is FilePart => part.type === "file")
    .map((filePart): AttachmentBubbleRowItem => {
      const name = filePart.filename ?? filePart.name ?? "attachment";
      const mediaType = filePart.mediaType ?? "";
      return {
        kind: mediaType.startsWith("image/")
          ? "image"
          : mediaType === "text/csv" || name.toLowerCase().endsWith(".csv")
            ? "csv"
            : "pdf",
        name,
        url: typeof filePart.url === "string" ? filePart.url : undefined,
      };
    });
  const hasUserText = userText !== null && userText.trim().length > 0;

  const userBubble =
    hasUserText && userText != null ? (
      wrapUserBubble ? (
        <div className="flex w-full min-w-0 justify-end">
          <UserBubble>{userText}</UserBubble>
        </div>
      ) : (
        <UserBubble>{userText}</UserBubble>
      )
    ) : null;

  return (
    <div className="flex w-full min-w-0 flex-col gap-1.5">
      <AttachmentBubbleRow attachments={attachments} />
      {userBubble}
    </div>
  );
};

interface HeraldChatSettledMessageRowsProps {
  messages: UIMessage[];
  conversationId?: string;
  wrapUserBubble?: boolean;
}

/** Completed turns only; props stay stable while the latest assistant streams. */
const HeraldChatSettledMessageRows = ({
  messages,
  conversationId,
  wrapUserBubble,
}: HeraldChatSettledMessageRowsProps) =>
  messages.map((message) => {
    if (message.role === "system") {
      return null;
    }

    if (message.role === "user") {
      return (
        <HeraldChatUserMessageRow
          key={message.id}
          message={message}
          wrapUserBubble={wrapUserBubble}
        />
      );
    }

    return (
      <AssistantMessageBubble
        key={message.id}
        conversationId={conversationId}
        createdAt={getMessageCreatedAt(message)}
        parts={getMessageParts(message)}
        isLatestAssistantStreaming={false}
      />
    );
  });

interface HeraldChatStreamingAssistantRowProps {
  message: UIMessage;
  conversationId?: string;
}

/** In-flight assistant turn; re-renders on each streaming chunk. */
const HeraldChatStreamingAssistantRow = ({
  message,
  conversationId,
}: HeraldChatStreamingAssistantRowProps) => (
  <AssistantMessageBubble
    key={message.id}
    conversationId={conversationId}
    parts={getMessageParts(message)}
    isLatestAssistantStreaming
  />
);

export interface HeraldChatMessageListProps {
  messages: UIMessage[];
  isStreaming: boolean;
  showPendingThinking: boolean;
  conversationId?: string;
  streamingQuipMessage: string | null;
  /** Shows the thinking row from a dropped stream until the resumed stream delivers its first chunk. */
  isStreamConnectionLost?: boolean;
  /** When true, wraps user text in a right-aligned flex container (in-app panel). */
  wrapUserBubble?: boolean;
  className?: string;
  /** Holds the list height in px while a reconnected stream rebuilds the answer. */
  minHeight?: number;
  endRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Renders chat turns with the active assistant bubble isolated from settled history
 * so streaming updates do not re-render prior messages.
 */
export const HeraldChatMessageList = ({
  messages,
  isStreaming,
  showPendingThinking,
  conversationId,
  streamingQuipMessage,
  isStreamConnectionLost = false,
  wrapUserBubble = false,
  className,
  minHeight,
  endRef,
}: HeraldChatMessageListProps) => {
  const { settledMessages, streamingAssistant } =
    useStableSettledAndStreamingMessages({
      messages,
      isStreaming,
      showPendingThinking,
    });

  return (
    <div className={cn(className)} style={{ minHeight }}>
      <HeraldChatSettledMessageRows
        messages={settledMessages}
        conversationId={conversationId}
        wrapUserBubble={wrapUserBubble}
      />
      {streamingAssistant != null ? (
        <HeraldChatStreamingAssistantRow
          message={streamingAssistant}
          conversationId={conversationId}
        />
      ) : null}
      {showPendingThinking ? <ThinkingBubble /> : null}
      {isStreamConnectionLost ? (
        <ThinkingBubble />
      ) : (
        <StreamingQuipRow message={streamingQuipMessage} />
      )}
      {endRef != null ? (
        <div ref={endRef} className="h-px w-full shrink-0" aria-hidden />
      ) : null}
    </div>
  );
};
